import { useSyncStore } from '../../state/sync.ts';
import { t } from '../../i18n/he.ts';

export function SyncBadge() {
  const { configured, account, busy, redirecting, dirty, lastOutcome, signIn, signOut, sync, reauthenticate } =
    useSyncStore();

  if (!configured) return null;

  /*
   * An expired session replaces the normal badge rather than sitting beside it.
   * Nothing else here can succeed until it is dealt with, and the previous
   * behaviour — a generic failure with a sync button that keeps not working —
   * is what left signing out and back in to be discovered by hand.
   */
  if (account !== null && lastOutcome?.status === 'auth-expired') {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-amber-500/40 bg-amber-500/10 p-1 pe-2 text-xs backdrop-blur-xs">
        <button
          type="button"
          disabled={redirecting}
          onClick={() => void reauthenticate()}
          title={t.sync.authExpired}
          className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-2.5 py-1 text-xs font-semibold text-amber-950 transition-all hover:bg-amber-400 disabled:opacity-40"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <span>{redirecting ? t.sync.redirecting : t.sync.reauthenticate}</span>
        </button>
        <span className="max-w-[120px] truncate font-medium text-amber-200" title={t.sync.authExpired}>
          {account.name ?? account.username}
        </span>
      </div>
    );
  }

  if (account === null) {
    return (
      <button
        type="button"
        disabled={busy || redirecting}
        onClick={() => void signIn()}
        className="flex items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-indigo-500 transition-all disabled:opacity-40"
      >
        <svg className="h-4 w-4 text-indigo-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
        </svg>
        <span>{redirecting ? t.sync.redirecting : t.sync.signIn}</span>
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-slate-800 bg-slate-800/80 p-1 pe-2 backdrop-blur-xs text-xs dark:border-slate-800/80 dark:bg-slate-900/70">
      <button
        type="button"
        disabled={busy || redirecting}
        onClick={() => void sync()}
        className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-500 transition-all disabled:opacity-40 shadow-2xs"
        title={t.sync.syncNow}
      >
        {busy ? (
          <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
        ) : (
          <span className={`h-2 w-2 rounded-full ${dirty ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
        )}
        <span>{busy ? t.sync.syncing : t.sync.syncNow}</span>
      </button>

      <span className="max-w-[120px] truncate text-slate-300 font-medium" title={account.name ?? account.username}>
        {account.name ?? account.username}
      </span>

      <button
        type="button"
        disabled={busy || redirecting}
        onClick={() => void signOut()}
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-700 hover:text-rose-400 transition-all"
        title={t.sync.signOut}
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
      </button>
    </div>
  );
}

